import colors from "../../assets/colors"
import ModalHeader from "../common/ModalHeader"
import Icons from "../../assets/Icons"
import ImageIcon from "../common/ImageIcon"
import CustomButton from "../common/CustomButton"
import { useEffect, useState } from "react"
import ControlledInput from "../common/ControlledInput"
import { SubmitHandler, useForm } from "react-hook-form"
import ControlledDatePicker from "../common/ControlledDatePicker"
import ControlledGoogleAutoComplete from "../common/ControlledGoogleAutoComplete"
import ControlledPicker from "../common/ControlledPicker"
import ControlledSlider from "../common/ControlledSlider"
import { useToggle, useUpdateEffect } from "react-use"
import CustomCheckbox from "../common/CustomCheckbox"
import ControlledFileInput from "../common/ControlledFileInput"
import Event from '../../types/Event';
import useEvents from "../../hooks/useEvents"
import moment from "moment"
import useModal from "../../hooks/useModal"
import Alert, { AlertModalProps } from "./Alert"
import { cleanObject } from "../../common/Utils"

export type AddEventModalProps = {
    event: Event
}

type FieldValues = {
    date: Date
    endDate: Date
    name: string
    description: string
    location: {
        coordinates: {
            latitude: number,
            longitude: number
        },
        address: string,
        geohash: string
    }
    code: string
    status: 'active' | 'inactive' | 'archived'
    access: 'locked' | 'open'
    partner: string
    venue: string
    goals: {
        co2: number
    }
    eventLogo: string
    images: {
        image1: string
        image2: string
        image3: string
        image4: string
        image5: string
    }
    expectedTurnout: { min: number, max: number }
}

const AddEvent = ({ hide, props }: { hide: () => void, props: AddEventModalProps }) => {

    const { show: showAlertModal, ModalComp: AlertModal } = useModal<AlertModalProps>(({ hide, props }) => <Alert hide={hide} props={props} />)

    const { addEvent, deleteEvent, updateEvent } = useEvents()
    const { register, control, formState: { errors, dirtyFields }, watch, setValue, handleSubmit, } = useForm<FieldValues>({
        defaultValues: {
            ...props?.event,
            date: new Date(props?.event?.date || Date.now()),
            endDate: new Date(props?.event?.endDate || Date.now())
        }
    })
    const formValues = watch()

    const [addLogo, toggleLogoInput] = useToggle(!!props?.event?.eventLogo)
    const [useLogoColors, toggleUseLogoColor] = useToggle(false)
    const [addImages, toggleImageInput] = useToggle(!!props?.event?.images && Object.values(props.event.images).reduce((t, c) => !!t || !!c, false))
    const [addEventBounties, toggleEventBounties] = useToggle(false)
    const [enableMinting, toggleEnableMinting] = useToggle(!!props?.event?.minting_enabled)
    const [enableCheckIn, toggleEnableCheckIn] = useToggle(!!props?.event?.check_in_enabled)
    const [enableLeaderboards, toggleLeaderboards] = useToggle(!!props?.event?.leaderboards_enabled)
    const [featured, toggleFeatured] = useToggle(!!props?.event?.featured)
    const [enableMeetups, toggleMeetups] = useToggle(!!props?.event?.meetups_enabled)

    useUpdateEffect(() => {
        if (formValues.expectedTurnout) setValue('goals.co2', formValues.expectedTurnout.min + formValues.expectedTurnout.max)
    }, [JSON.stringify(formValues.expectedTurnout)])

    const onSubmit: SubmitHandler<FieldValues> = (data) => {
        hide()
        console.log({
            ...data,
            date: data.date.getTime(),
            endDate: data.endDate.getTime(),
        })
        if (!props.event) {
            addEvent({
                ...data,
                date: (data.date || new Date()).getTime(),
                endDate: (data.endDate || new Date()).getTime(),
                endTime: moment(data.endDate).format('hh:mm'),
                startTime: moment(data.date).format('hh:mm'),
                minting_enabled: enableMinting,
                check_in_enabled :enableCheckIn,
                leaderboards_enabled : enableLeaderboards,
                featured,
                meetups_enabled :enableMeetups
            })
        } else {
            const obj = {
                ...data,
                date: (data.date || new Date()).getTime(),
                endDate: (data.endDate || new Date()).getTime(),
                endTime: moment(data.endDate).format('hh:mm'),
                startTime: moment(data.date).format('hh:mm'),
                minting_enabled: enableMinting,
                check_in_enabled :enableCheckIn,
                leaderboards_enabled : enableLeaderboards,
                featured,
                meetups_enabled :enableMeetups
            }
            console.log(cleanObject(obj))
            updateEvent(cleanObject(obj), props.event.id,)
        }
    }

    const onDelete = () => {
        showAlertModal({
            title: 'Delete',
            subTitle: 'Are you sure you want to delete?',
            buttons: [{ text: 'Cancel' }, { text: 'Yes', onClick: () => (deleteEvent(props.event.id), hide()) }]
        })
    }

    const onArchive = () => {
        showAlertModal({
            title: 'Archive',
            subTitle: 'Are you sure you want to archive the event?',
            buttons: [{ text: 'Cancel' }, {
                text: 'Yes', onClick: () => {
                    hide()
                    updateEvent({ status: 'archived' }, props.event.id)
                }
            }]
        })
    }

    return (
        <div style={{ margin: -30 }}>
            {AlertModal}
            <ModalHeader style={{ padding: 20 }} hide={hide} title="Submit Event" />

            <div style={{ height: 1, background: colors.BLACK_20 }} />

            <div style={{ overflowY: 'scroll', flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 20, maxHeight: '75vh', padding: 30 }} >

                <p style={{ fontWeight: '500', fontSize: 14, marginBottom: 10 }} >Event Details</p>
                <p style={{ fontSize: 10, opacity: .7, maxWidth: 400, marginBottom: 20 }} >Let’s have accurate details about the event. This is used to populate our custom event descriptions to give credibility to events on our platform.</p>

                <ControlledInput
                    label='Name'
                    inputProps={{ placeholder: 'Add event name here...' }}
                    containerStyle={{ marginBottom: 15 }}
                    formProps={{ register, errors, name: 'name', options: { required: true } }} />

                <ControlledInput
                    label='Description'
                    multiline
                    inputProps={{ placeholder: 'Add event description here...', style: { height: 100 } }}
                    containerStyle={{ marginBottom: 15 }}
                    formProps={{ register, errors, name: 'description', options: { required: true } }} />

                <div style={{ display: 'flex', marginBottom: 15 }} >
                    <ControlledDatePicker
                        containerStyle={{ flex: 1 }}
                        label='Start Date'
                        formProps={{ errors, name: 'date', options: { required: true }, control }} />
                    <div style={{ width: 10 }} />
                    <ControlledDatePicker
                        containerStyle={{ flex: 1 }}
                        label='End Date'
                        formProps={{ errors, name: 'endDate', options: { required: true }, control }} />
                </div>

                <ControlledInput
                    label='Venue'
                    containerStyle={{ marginBottom: 15 }}
                    inputProps={{ placeholder: 'Ex. Secret gardens' }}
                    formProps={{ register, errors, name: 'venue', options: { required: true } }} />

                <ControlledGoogleAutoComplete
                    label='Location'
                    containerStyle={{ marginBottom: 15 }}
                    formProps={{ errors, name: 'location', isDirty: dirtyFields?.location?.address, options: { required: true }, control }} />

                <div style={{ display: 'flex', marginBottom: 15 }} >
                    <ControlledPicker
                        label='Status'
                        containerStyle={{ flex: 1 }}
                        options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }, ...(props.event ? [{ label: 'Archived', value: 'archived' }] : [])]}
                        formProps={{ errors, name: 'status', options: { required: true }, control }} />
                    <div style={{ width: 10 }} />
                    <ControlledPicker
                        label='Locked'
                        containerStyle={{ flex: 1 }}
                        options={[{ label: 'Locked', value: 'locked' }, { label: 'Open', value: 'open' }]}
                        formProps={{ errors, name: 'access', options: { required: true }, control }} />
                </div>

                {formValues.access == 'locked' && <ControlledInput
                    label='Code'
                    inputProps={{ placeholder: '* * * * * *' }}
                    containerStyle={{ marginBottom: 15 }}
                    formProps={{ register, errors, name: 'code', options: { required: true } }} />}

                <ControlledPicker
                    label='Expected Turnout'
                    containerStyle={{ flex: 1, marginBottom: 15 }}
                    options={expectedTurnouts}
                    formProps={{ errors, name: 'expectedTurnout', options: { required: true }, control }} />

                {formValues.expectedTurnout && <div >

                    <p style={{ color: colors.BLACK_100, fontSize: 12, fontWeight: '500', marginBottom: 6 }} >Expected CO2 Saving Goal</p>
                    <p style={{ fontSize: 10, opacity: .5, maxWidth: 400, }} >The average carpool user will save 3 kgs in CO2 by Carpooling for this event. We’d love to know what your goals are with respect to CO2 savings.</p>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginTop: 25, marginBottom: 25, }}>
                        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 60 }} >
                            <img src={Icons.hexagon_2} style={{ width: 60, position: 'absolute', zIndex: -1 }} />
                            <p style={{}} >{formValues.goals?.co2}</p>
                            <p style={{ fontSize: 10 }} >Kgs</p>
                        </div>

                        <div style={{ flex: 1 }} >
                            <ControlledSlider
                                min={formValues.expectedTurnout.min * 2}
                                max={formValues.expectedTurnout.max * 2}
                                formProps={{ errors, name: 'goals.co2', options: { required: true }, control }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15, alignItems: 'center' }} >
                                <p style={{ fontSize: 13, fontWeight: '500' }} >{formValues.expectedTurnout.min * 2} Kgs</p>
                                <p style={{ fontSize: 13, backgroundColor: colors.BLACK_10, color: colors.BLACK_30, padding: 10, borderRadius: 10 }} >Expected CO2 Savings</p>
                                <p style={{ fontSize: 13, fontWeight: '500' }} >{formValues.expectedTurnout.max * 2} Kgs</p>
                            </div>
                        </div>

                    </div>

                </div>}

                <div>
                    <CustomCheckbox containerStyle={{ marginBottom: 15 }} value={addLogo} label='Add Organization Logo' onClick={toggleLogoInput} />
                    {addLogo && <ControlledFileInput containerStyle={{ marginBottom: 15 }} formProps={{ errors, name: 'eventLogo', options: { required: false }, control }} />}
                </div>

                {addLogo && <CustomCheckbox containerStyle={{ marginBottom: 15 }} value={useLogoColors} label='Create skin colors from logo' onClick={toggleUseLogoColor} />}

                <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15 }} >
                        <CustomCheckbox containerStyle={{ marginRight: 6 }} value={addImages} onClick={toggleImageInput} />
                        <p style={{ color: colors.BLACK_100, fontSize: 12, fontWeight: '500', }} >Add Images</p>
                    </div>
                    {addImages && <div style={{ display: 'flex', marginBottom: 15, gap: 15 }} >
                        <ControlledFileInput containerStyle={{ flex: 1 }} formProps={{ errors, name: 'images.image1', options: { required: false }, control }} />
                        <ControlledFileInput containerStyle={{ flex: 1 }} formProps={{ errors, name: 'images.image2', options: { required: false }, control }} />
                        <ControlledFileInput containerStyle={{ flex: 1 }} formProps={{ errors, name: 'images.image3', options: { required: false }, control }} />
                        <ControlledFileInput containerStyle={{ flex: 1 }} formProps={{ errors, name: 'images.image4', options: { required: false }, control }} />
                        <ControlledFileInput containerStyle={{ flex: 1 }} formProps={{ errors, name: 'images.image5', options: { required: false }, control }} />
                    </div>}
                </div>

                {/* <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 15, }} >
                        <CustomCheckbox containerStyle={{ marginRight: 6 }} value={addEventBounties} onClick={toggleEventBounties} />
                        <p style={{ color: colors.BLACK_100, fontSize: 12, fontWeight: '500', }} >Add Event Bounties</p>
                        <p style={{ color: colors.BLACK_60, fontSize: 8, paddingTop: 3, paddingLeft: 2 }} >(Add incentives for highest ranking attendees on the leaderboard)</p>
                    </div>
                    {addEventBounties && <ControlledInput
                        containerStyle={{ marginBottom: 20 }}
                        inputProps={{ placeholder: 'Bounty for Venue 1' }}
                        formProps={{ register, errors, name: 'venue', options: { required: true } }} />}
                </div> */}

                <CustomCheckbox label="Enable Leaderboards" containerStyle={{ marginRight: 6, marginBottom : 15 }} value={enableLeaderboards} onClick={toggleLeaderboards} />

                <CustomCheckbox label="Featured" containerStyle={{ marginRight: 6, marginBottom : 15 }} value={featured} onClick={toggleFeatured} />

                <CustomCheckbox label="Enable Minting Proof of Impact NFT" containerStyle={{ marginRight: 6, marginBottom : 15 }} value={enableMinting} onClick={toggleEnableMinting} />

                <CustomCheckbox label="Enable Proof of Attendance (Check In)" containerStyle={{ marginRight: 6,  marginBottom : 15 }} value={enableCheckIn} onClick={toggleEnableCheckIn} />

                <CustomCheckbox label="Enable Meetups" containerStyle={{ marginRight: 6 }} value={enableMeetups} onClick={toggleMeetups} />

            </div>

            <div style={{ height: 1, background: colors.BLACK_20 }} />

            <div style={{ display: 'flex', alignItems: 'center', padding: 20, gap: 10 }} >
                <CustomButton
                    onClick={handleSubmit(onSubmit)}
                    containerStyle={{ flex: 1 }}
                    label={props.event ? "Update" : "Create"} />
                {props.event && props.event.status === 'active' && <CustomButton
                    onClick={onArchive}
                    containerStyle={{ background: colors.BLACK_80, paddingLeft: 40, paddingRight: 40 }}
                    label="Archive" />}
                {props.event && <CustomButton
                    onClick={onDelete}
                    containerStyle={{ background: colors.RED, paddingLeft: 40, paddingRight: 40 }}
                    label="Delete" />}
            </div>


        </div>
    )
}

export default AddEvent

const expectedTurnouts = [
    { label: '0-50', value: { max: 50, min: 0 } },
    { label: '50-100', value: { max: 100, min: 50 } },
    { label: '100-200', value: { max: 200, min: 100 } },
    { label: '200-500', value: { max: 500, min: 200 } },
    { label: '500-1000', value: { max: 1000, min: 500 } },
    { label: '1000+', value: { max: 10000, min: 1000 } }
]